'use client';

import { useTranslations } from 'next-intl';

import { UiKitButtonBase } from '@/fo/ui/kit/button/base';
import { UiKitDrawerBase } from '@/fo/ui/kit/drawer/base';
import { UiKitToggleBase } from '@/fo/ui/kit/toggle/base';

import type { CookieConsentGroups } from '../../types';

import { styles } from './Settings.styles';

export interface SettingsProps {
  open: boolean;
  groups: CookieConsentGroups;
  onGroupsChange: (groups: CookieConsentGroups) => void;
  onSave: () => void;
  onCancel: () => void;
  onClose: () => void;
}

export function Settings({
  open,
  groups,
  onGroupsChange,
  onSave,
  onCancel,
  onClose
}: SettingsProps) {
  const slots = styles();
  const t = useTranslations('cookieConsent.settings');

  return (
    <UiKitDrawerBase
      open={open}
      title={t('title')}
      direction="right"
      onOpenChange={(value) => !value && onClose()}
      contentClassName={slots.base()}
      withoutMobile
    >
      <strong className={slots.title()}>{t('title')}</strong>
      <p className={slots.description()}>{t('description')}</p>

      <div className={slots.list()}>
        <div className={slots.item()}>
          <div className={slots.itemContent()}>
            <strong className={slots.itemTitle()}>
              {t('necessary.title')}
            </strong>
            <p
              className={slots.itemDescription()}
              dangerouslySetInnerHTML={{ __html: t('necessary.description') }}
            />
          </div>
          <UiKitToggleBase
            checked
            disabled
            className={slots.toggle()}
          />
        </div>

        <div className={slots.item()}>
          <div className={slots.itemContent()}>
            <strong className={slots.itemTitle()}>
              {t('analytical.title')}
            </strong>
            <p
              className={slots.itemDescription()}
              dangerouslySetInnerHTML={{ __html: t('analytical.description') }}
            />
          </div>
          <UiKitToggleBase
            checked={groups.analytical}
            className={slots.toggle()}
            onChange={(analytical) => onGroupsChange({ ...groups, analytical })}
          />
        </div>

        <div className={slots.item()}>
          <div className={slots.itemContent()}>
            <strong className={slots.itemTitle()}>
              {t('functional.title')}
            </strong>
            <p
              className={slots.itemDescription()}
              dangerouslySetInnerHTML={{ __html: t('functional.description') }}
            />
          </div>
          <UiKitToggleBase
            checked={groups.functional}
            className={slots.toggle()}
            onChange={(functional) => onGroupsChange({ ...groups, functional })}
          />
        </div>

        <div className={slots.item()}>
          <div className={slots.itemContent()}>
            <strong className={slots.itemTitle()}>
              {t('marketing.title')}
            </strong>
            <p
              className={slots.itemDescription()}
              dangerouslySetInnerHTML={{ __html: t('marketing.description') }}
            />
          </div>
          <UiKitToggleBase
            checked={groups.marketing}
            className={slots.toggle()}
            onChange={(marketing) => onGroupsChange({ ...groups, marketing })}
          />
        </div>
      </div>

      <div className={slots.buttons()}>
        <UiKitButtonBase
          color="secondary"
          className={slots.button({ className: slots.cancel() })}
          onClick={onCancel}
        >
          {t('cancel')}
        </UiKitButtonBase>
        <UiKitButtonBase
          className={slots.button()}
          onClick={onSave}
        >
          {t('save')}
        </UiKitButtonBase>
      </div>
    </UiKitDrawerBase>
  );
}
